import { Markdown, Themes } from '@mgmresorts/markdown';
import {
  ApiErrors,
  AuthTransaction,
  IdxTransaction,
  MGMAuthError,
  IdxStep,
  AuthenticatorKey,
} from '@mgmresorts/mgm-auth-js-sdk';
import { TextInput, TextLink, Alert } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { OktaUser } from '../../../@types/okta-user';
import { KNOWN_ERROR_CODES } from '../../api/constants';
import { UNKNOWN_ERROR_MESSAGE } from '../../api/errors';
import { useClientIntegrationContent } from '../../common/api';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useMfaEnabled, usePasskeyEnabled, useEnablePostSignIn2faEnrollment } from '../../hooks';
import { useAppRedirect } from '../../hooks/useAppRedirect';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useIdxApiEnabled } from '../../hooks/useIdxApiEnabled';
import { useInput } from '../../hooks/useInput';
import { useLoadingLabel } from '../../hooks/useLoadingLabel';
import { captureEvent, captureException, captureMessage } from '../../monitoring/logrocket';
import { EventAttributes } from '../../utils/analytics/types';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { authWithRedirect } from '../../utils/auth/authWithRedirect';
import {
  ClientId,
  IDENTITY_WIDGET_PATHS,
  IDW_FLOW_PARAM,
  MaxLengths,
  TWO_FACTOR_ENROLLMENT_PROMPTED_KEY,
} from '../../utils/constants';
import { getFeaturesByClientId } from '../../utils/getFeaturesByClientId';
import { linkAccountToPartner } from '../../utils/linkAccountToPartner';
import { canUsePasskey, getPasskeySupportDetails } from '../../utils/passkey';
import { usePersistentParameters } from '../../utils/persistParameterContext';
import { UserAccountContext } from '../../utils/userAccountContext';
import { emailValidator, passwordValidator } from '../../utils/validators';
import { ResetPasswordForm } from '../ResetPassword/components/ResetPasswordForm';
import { StyledContent as ResetPasswordStyledContent } from '../ResetPassword/styles';

import { StyledButton, StyledHeading, StyledLegalText, StyledForgotPasswordLink, StyledAlertContainer } from './styles';

export interface SignInProps {
  /**
   * The type of sign-in flow to display
   * - 'default': Shows password input, forgot password link, and Sign in CTA (regular sign-in flow)
   * - 'auth-selection': Shows "Sign in with passkey" and "Sign in with password" CTAs (primary authentication selection flow)
   */
  type?: 'default' | 'auth-selection';
}

interface MfaSignInResult {
  success: boolean;
  error?: string;
  transaction?: IdxTransaction; // IDX transaction details for debugging
  status?: string; // PENDING, SUCCESS, etc.
}

export function SignIn({ type = 'default' }: SignInProps) {
  usePageLoadEvent('sign in', 'home');
  const { email, preferredFactor, setHasEmailEnrolled, setHasPhoneEnrolled, hasPasskeyEnrolled, mgmId } =
    useContext(UserAccountContext);
  const { params } = usePersistentParameters();
  const { client_id, redirect_uri } = params;
  const trackEvent = useEvent();
  const redirectToApp = useAppRedirect();
  const [error, setErrorState] = useState<null | string>(null);
  const [isLoading, setLoadingState] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const { validateField, invalidFields } = useFieldValidation([emailValidator, passwordValidator]);
  const { authClient } = useAuthContext();
  const { getContentByPath } = useContent();
  const [authTransaction, setAuthTransaction] = useState<AuthTransaction | undefined>();
  const [postLoginStep, setPostLoginStep] = useState<'redirect' | 'password_reset' | 'password_reset:error'>(
    'redirect',
  );
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);

  // Auth-selection mode specific state
  const [isLoadingPasskey, setLoadingPasskeyState] = useState(false);
  const [isLoadingPassword, setLoadingPasswordState] = useState(false);
  const [isPasskeySupported, setIsPasskeySupported] = useState<boolean | null>(null);

  const {
    bind: bindPassword,
    value: password,
    setValue: setPasswordValue,
  } = useInput('', 'password', validateField, validateField);
  const { bind: bindUsername, value: login } = useInput(email || '', 'email', validateField, validateField);

  const enableIdxApi = useIdxApiEnabled();
  const signInCTA = useLoadingLabel(isLoading, 'signIn.signIn');
  const signInWithPasskey = useLoadingLabel(isLoadingPasskey, 'signIn.signInWithPasskey');
  const signInWithPassword = useLoadingLabel(isLoadingPassword, 'signIn.signInWithPassword');

  const { isBypassGuidedSignIn, isHideForgotPasswordLink, isAllowOktaSignInWithRedirect } = getFeaturesByClientId(
    client_id as ClientId,
  );

  const { mfaEnabled } = useMfaEnabled();
  const { passkeyEnabled } = usePasskeyEnabled();
  const { enablePostSignIn2faEnrollment } = useEnablePostSignIn2faEnrollment();

  const {
    disclaimer: legalText,
    authFailedError,
    lockedOutError,
  } = useClientIntegrationContent(client_id as string | undefined);

  if (!email && !isBypassGuidedSignIn) {
    navigate('/', true);
  }
  // Clear stale MFA enrollment flag to prevent cross-user bypass on session timeout or sign-out
  useEffect(() => {
    sessionStorage.removeItem(TWO_FACTOR_ENROLLMENT_PROMPTED_KEY);
  }, []);

  useEffect(() => {
    trackEvent('identity – sign in - start', 'start sign in process', 'signinstart');
  }, [trackEvent]);

  // Check passkey support for auth-selection mode
  useEffect(() => {
    if (type === 'auth-selection') {
      const checkPasskeySupport = async () => {
        const isSupported = await canUsePasskey(authClient, mgmId);
        setIsPasskeySupported(isSupported);
      };
      checkPasskeySupport();
    }
  }, [type, authClient, mgmId]);

  const processSignInUsingMFA = useCallback(
    async (username: string, password: string): Promise<MfaSignInResult> => {
      try {
        await authClient.idx.startAuthenticate(username);
        await authClient.idx.stepHandlers[IdxStep.Identify].Identify(username);

        // Authenticate with password
        const passwordHandler = authClient.idx.stepHandlers[IdxStep.AuthPassword];
        await passwordHandler.passwordAuth(username, password);

        const transaction = authClient.idx.idxTransaction ?? undefined;
        const errorMessages = transaction?.messages?.filter(m => m.class === 'ERROR') || [];

        if (authClient.idx.nextIdxStep === IdxStep.Success) {
          await trackEvent(
            'identity – sign in - credentials submit success',
            'sign in credentials submit success',
            'signinsubmitsuccess',
            { signInState: 'success' },
            true,
          );
          authClient.idx.stepHandlers[IdxStep.Success].setTokens();
          if (mfaEnabled && enablePostSignIn2faEnrollment) {
            // Check if user has email/SMS 2FA enrolled - if not, redirect to add two factor page
            const hasEmailFactor = passwordHandler.canSelectAuth(AuthenticatorKey.OKTA_EMAIL);
            const hasPhoneFactor = passwordHandler.canSelectAuth(AuthenticatorKey.PHONE_NUMBER);

            if (!hasEmailFactor && !hasPhoneFactor) {
              // navigate(IDENTITY_WIDGET_PATHS.PostSignInEnrollment, true, { [IDW_FLOW_PARAM]: 'addTwoFactor' });
              navigate(IDENTITY_WIDGET_PATHS.MfaEnroll, true, { [IDW_FLOW_PARAM]: 'signin' });
              return { success: true, transaction: transaction };
            }
          }
          if (isAllowOktaSignInWithRedirect) {
            const redirectResult = await authClient.handleOktaSignInWithRedirect(transaction);
            if (redirectResult) {
              return redirectResult;
            }
          }

          redirectToApp();
          return { success: true, transaction: transaction };
        } else if (errorMessages.length > 0) {
          return { success: false, transaction: transaction };
        } else if (mfaEnabled) {
          const hasEmailEnrolled = passwordHandler.canSelectAuth(AuthenticatorKey.OKTA_EMAIL);
          const hasPhoneEnrolled = passwordHandler.canSelectAuth(AuthenticatorKey.PHONE_NUMBER);

          setHasEmailEnrolled(hasEmailEnrolled);
          setHasPhoneEnrolled(hasPhoneEnrolled);
          if (hasEmailEnrolled && hasPhoneEnrolled && preferredFactor) {
            switch (preferredFactor.toLowerCase()) {
              case 'sms': {
                await passwordHandler.selectAuth(AuthenticatorKey.PHONE_NUMBER);
                navigate(IDENTITY_WIDGET_PATHS.MfaAuthSms, true);
                return { success: true, transaction: transaction };
              }
              case 'email':
              default: {
                await passwordHandler.selectAuth(AuthenticatorKey.OKTA_EMAIL);
                navigate(IDENTITY_WIDGET_PATHS.MfaAuthEmail, true);
                return { success: true, transaction: transaction };
              }
            }
          }

          if (hasEmailEnrolled) {
            await passwordHandler.selectAuth(AuthenticatorKey.OKTA_EMAIL);
            navigate(IDENTITY_WIDGET_PATHS.MfaAuthEmail, true);
            return { success: true, transaction: transaction };
          }

          if (hasPhoneEnrolled) {
            await passwordHandler.selectAuth(AuthenticatorKey.PHONE_NUMBER);
            navigate(IDENTITY_WIDGET_PATHS.MfaAuthSms, true);
            return { success: true, transaction: transaction };
          }
          captureMessage('Next authentication step unknown, no MFA factors available', 'sign-in');
          return { success: false, transaction: transaction };
        } else {
          captureMessage(
            'MFA factors available, but MFA feature turned off – falling back to legacy sign-in',
            'sign-in',
          );
          return { success: false, transaction: transaction, error: 'mfa_disabled_fallback' };
        }
      } catch (idxError) {
        captureMessage('IDX flow error, falling back to traditional sign-in', 'sign-in');
        captureException(idxError instanceof Error ? idxError : new Error(String(idxError)), 'sign-in');
        return { success: false, transaction: authClient?.idx?.idxTransaction ?? undefined };
      }
    },
    [
      authClient,
      preferredFactor,
      trackEvent,
      redirectToApp,
      setHasEmailEnrolled,
      setHasPhoneEnrolled,
      enablePostSignIn2faEnrollment,
      mfaEnabled,
      isAllowOktaSignInWithRedirect,
    ],
  );

  const processSignIn = useCallback(
    async (username, password) => {
      try {
        setLoadingState(true);
        setErrorState(null);

        captureEvent('Sign In', {
          email: username,
          partner: String(params.partner),
        });

        let authResponse: MGMAuthError | void | null = null;
        let isLockedOut = false;
        let isInvalidCredentials = false;

        // Use the IDX in-app pipeline whenever the OKTA_IDX_API flag is enabled for
        // this client (via useIdxApiEnabled, which already gates on isIdxApiEnabled +
        // the OKTA_IDX_API launch flag). The IDX flow is used regardless of the
        // mfaEnabled feature toggle - when MFA is disabled, `processSignInUsingMFA`
        // skips the email/SMS verification steps and completes sign-in directly.
        // When enableIdxApi is false, we fall back to signInWithRedirect (via
        // authClient.signIn) - toggling the flag OFF instantly reverts to the
        // traditional redirect flow.
        if (enableIdxApi) {
          const idxResult = await processSignInUsingMFA(username, password);

          if (idxResult.success) {
            return;
          } else if (idxResult.error === 'mfa_disabled_fallback') {
            // IDX pipeline presented an MFA challenge but the ENABLE_MFA feature
            // flag is off — fall back to the legacy authClient.signIn() flow which
            // lets Okta handle the authentication via redirect.
            authResponse = await authClient.signIn({
              username,
              password,
            });
          } else {
            if (idxResult.transaction?.status === 'PENDING') {
              const messages = idxResult.transaction?.messages || [];
              let errorMessage = 'Authentication failed';
              if (messages.length) {
                const errorCodes = new Set(messages.filter(m => m.class === 'ERROR').map(m => m.i18n?.key));

                let errorCode = 'Authentication failed';
                if (errorCodes.has('errors.E0000119')) {
                  isLockedOut = true;
                  errorCode = 'E0000119';
                  // Default (non-partner) locked-out message; partner-specific message
                  // is applied below via `getLockedOutMessage()` when applicable.
                  errorMessage = getContentByPath('signIn.message.locked_out');
                } else if (errorCodes.has('errors.E0000004')) {
                  isInvalidCredentials = true;
                  errorCode = 'E0000004';
                  errorMessage = KNOWN_ERROR_CODES.E0000004;
                }
                authResponse = new MGMAuthError({ message: errorMessage, cause: { errorCode } });
              }
            }
            if (!authResponse) {
              authResponse = new MGMAuthError({ message: 'Authentication failed' });
            }
          }
        } else {
          authResponse = await authClient.signIn({
            username,
            password,
          });
        }

        const getLockedOutMessage = (): string => {
          // For third-party redirect clients, prefer the CMS-sourced partner-specific
          // locked-out message. Falls back to the generic bundled
          // locked-out copy when no partner-specific content is available.
          const message = isAllowOktaSignInWithRedirect
            ? lockedOutError
            : getContentByPath<string>('signIn.message.locked_out') || '';
          return String(message).replace('{REDIRECT_URI}', redirect_uri as string);
        };

        if (authResponse instanceof MGMAuthError && authResponse.transaction?.status === 'PASSWORD_EXPIRED') {
          captureMessage('user required to update password upon long', 'sign-in', { username });
          setAuthTransaction(authResponse.transaction);
          setPostLoginStep('password_reset');
          setLoadingState(false);
        } else if (
          (authResponse instanceof MGMAuthError && authResponse.transaction?.status === 'LOCKED_OUT') ||
          isLockedOut
        ) {
          captureMessage('user required to reset password upon long', 'sign-in', { username });
          setErrorState(getLockedOutMessage());
          setIsLockedOut(true);
          authResponse && captureException(authResponse, 'sign-in', { username });
          setLoadingState(false);
        } else if (authResponse instanceof MGMAuthError || isInvalidCredentials) {
          // TODO: Check if transaction status is pending which means MFA FF is disabled but user has MFA setup in Okta, so display readable error message
          if (
            authResponse instanceof MGMAuthError &&
            isAllowOktaSignInWithRedirect &&
            (authResponse?.cause as { errorCode?: string })?.errorCode === ApiErrors.authenticationFailed.errorCode
          ) {
            authResponse.message = authFailedError.replace('{REDIRECT_URI}', redirect_uri as string);
          }

          trackEvent(
            getContentByPath<string>('mfa.analytics.error.eventName'),
            'sign in credentials submit error',
            getContentByPath<string>('mfa.analytics.error.trackSuffix'),
            { errorDisplayed: authResponse?.message },
          );
          setErrorState(authResponse?.message || 'Authentication Error');
          authResponse && captureException(authResponse, 'sign-in', { username });
          setLoadingState(false);
        } else {
          await trackEvent(
            'identity – sign in - credentials submit success',
            'sign in credentials submit success',
            'signinsubmitsuccess',
            { signInState: 'success' },
            true,
          );

          if (params.partner && params.tokenKey) {
            const activeSessionObj = await authClient.hasActiveMemberSession();
            if (activeSessionObj?.activeMemberSession && activeSessionObj?.session?.user) {
              const user = (await activeSessionObj.session.user()) as OktaUser;
              await linkAccountToPartner(params.partner as string, params.tokenKey as string, user.profile.mlifeNumber);
            }
          }

          /**
           * TODO: Implement cleaner solution
           * For BetMGM Okta will handle redirect using signInWithRedirect method in AuthSDK
           */
          if (isAllowOktaSignInWithRedirect) {
            return;
          }

          redirectToApp();
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'Operation Timeout') {
          // last ditch effort to attempt to log the user in.
          authWithRedirect(
            username,
            password,
            authClient,
            async () => {
              await trackEvent(
                'identity – sign in - credentials submit success',
                'sign in credentials submit success',
                'signinsubmitsuccess',
                { signInState: 'success' },
                true,
              );
            },
            () => {
              trackEvent(
                getContentByPath<string>('mfa.analytics.error.eventName'),
                'sign in credentials submit error',
                getContentByPath<string>('mfa.analytics.error.trackSuffix'),
                { errorDisplayed: UNKNOWN_ERROR_MESSAGE },
              );
              captureException(err as Error, 'sign-in', { username });
              setErrorState(UNKNOWN_ERROR_MESSAGE);
              setLoadingState(false);
            },
          );
        } else {
          trackEvent(
            getContentByPath<string>('mfa.analytics.error.eventName'),
            'sign in credentials submit error',
            getContentByPath<string>('mfa.analytics.error.trackSuffix'),
            { errorDisplayed: UNKNOWN_ERROR_MESSAGE },
          );
          captureException(err as Error, 'sign-in', { username });
          setErrorState(UNKNOWN_ERROR_MESSAGE);
          setLoadingState(false);
        }
      }
    },
    [
      params.partner,
      params.tokenKey,
      authClient,
      isAllowOktaSignInWithRedirect,
      getContentByPath,
      redirect_uri,
      trackEvent,
      redirectToApp,
      enableIdxApi,
      processSignInUsingMFA,
      authFailedError,
      lockedOutError,
    ],
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      try {
        setLoadingState(true);
        if (authTransaction && authTransaction?.changePassword) {
          const result = await authTransaction.changePassword({ oldPassword: password, newPassword });
          // TODO: add effect for failure here based on transaction state
          if (result.status === 'SUCCESS') {
            captureMessage('password update upon sign in processed sucessfully', 'sign-in-password-update', {
              username: login,
            });
            setPasswordValue(newPassword);
            processSignIn(login, newPassword);
            return;
          }
        }

        throw Error('Invalid transaction state, password reset required upon login');
      } catch (err) {
        captureException(err as Error, 'sign-in-password-update', { username: login });
        setErrorState('Password update failed, please sign in again');
      } finally {
        setLoadingState(false);
      }
    },
    [authTransaction, password, setPasswordValue, processSignIn, login],
  );

  // Auth-selection mode functions
  const processSignInWithPassword = useCallback(async () => {
    setErrorState(null);
    setLoadingPasswordState(true);
    await trackEvent('identity – unified MFA CTAs', 'Sign in with password', 'unifiedmfactas', {}, true);
    navigate('/signin', true);
  }, [trackEvent]);

  const processSignInWithPasskey = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Sign in with passkey', 'unifiedmfactas', {}, true);
    try {
      setErrorState(null);
      setLoadingPasskeyState(true);

      // Check if passkey feature flag is enabled
      if (!passkeyEnabled) {
        throw new Error('Passkey authentication is currently disabled. Please sign in with password.');
      }

      if (!email) {
        throw new Error('Email is required for passkey authentication');
      }

      // CRITICAL: Check device passkey support BEFORE attempting ANY authentication
      console.log('🔐 Checking passkey support before authentication...');
      const deviceSupportsPasskeys = await canUsePasskey(authClient, mgmId);
      const supportDetails = await getPasskeySupportDetails();

      console.log('🔐 Device passkey support:', deviceSupportsPasskeys);
      console.log('🔐 Passkey support details:', supportDetails);

      // STOP IMMEDIATELY if device doesn't support passkeys - DO NOT proceed with IDX flow
      if (!deviceSupportsPasskeys) {
        console.log('🚫 Device does not support passkeys, showing error message instead of attempting authentication');
        throw new Error(
          `Passkeys are not supported on this ${supportDetails.platform} device using ${supportDetails.browser}. Please sign in with password instead.`,
        );
      }

      // Additional check: Verify WebAuthn is actually available
      if (!window.PublicKeyCredential) {
        console.log('🚫 PublicKeyCredential not available, preventing passkey authentication');
        throw new Error('Passkeys are not available in this browser. Please sign in with password instead.');
      }

      console.log('✅ Device supports passkeys, proceeding with authentication...');
      // Only proceed with IDX flow if device supports passkeys
      await authClient.idx.startAuthenticate(email);
      await authClient.idx.stepHandlers[IdxStep.Identify].Identify(email);

      // Get the passkey auth handler and check if passkey is available for this account
      const passkeyAuthHandler = authClient.idx.stepHandlers[IdxStep.AuthPasskey];

      if (!passkeyAuthHandler.canSelectAuth(AuthenticatorKey.WEBAUTHN)) {
        console.log('🚫 Passkey not available for this account');
        throw new Error('Passkey is not available for this account. Please use password authentication.');
      }

      console.log('🔐 Starting passkey authentication...');
      // Perform passkey authentication with specific error handling for missing credentials
      try {
        await passkeyAuthHandler.passkeyAuth();
      } catch (passkeyError) {
        console.log('🚨 Passkey authentication failed:', passkeyError);
        // Handle the specific case where no passkeys are registered locally
        if (passkeyError instanceof Error && passkeyError.name === 'NotAllowedError') {
          // This usually means no passkeys are registered for this domain
          throw new Error(
            'No passkeys are registered for this account on this device. Please enroll a passkey first or sign in with password.',
          );
        } else if (passkeyError instanceof Error && passkeyError.name === 'AbortError') {
          throw new Error('Passkey authentication was cancelled. Please try again or sign in with password.');
        } else if (passkeyError instanceof Error && passkeyError.name === 'NotSupportedError') {
          throw new Error('Passkeys are not supported on this device. Please sign in with password.');
        } else {
          // Re-throw the original error to be handled by outer catch block
          throw passkeyError;
        }
      }

      const transaction = authClient.idx.idxTransaction ?? undefined;

      // Check if authentication was successful
      if (authClient.idx.nextIdxStep === IdxStep.Success) {
        await trackEvent(
          'identity – sign in - credentials submit success',
          'sign in credentials submit success',
          'signinsubmitsuccess',
          { signInState: 'success' },
          true,
        );
        authClient.idx.stepHandlers[IdxStep.Success].setTokens();
        if (isAllowOktaSignInWithRedirect) {
          const redirectResult = await authClient.handleOktaSignInWithRedirect(transaction);
          if (redirectResult) {
            return redirectResult;
          }
        }
        console.log('✅ Passkey authentication successful!');
        trackEvent('identity – passkey auth success', 'passkey auth success', 'passkeyauthsuccess', {}, true);
        redirectToApp();
      } else {
        console.log('❌ Passkey authentication did not complete successfully');
        throw new Error('Passkey authentication did not complete successfully.');
      }
    } catch (error) {
      console.error('🚨 Passkey authentication error:', error);

      let errorMessage = 'Passkey authentication failed. ';
      if (error instanceof Error) {
        if (error.message.includes('not supported')) {
          errorMessage = 'Passkeys are not supported on this device. Please sign in with password.';
        } else if (error.message.includes('not available')) {
          errorMessage = 'No passkey is available for this account on this device. Please sign in with password.';
        } else if (error.message.includes('cancelled')) {
          errorMessage = 'Passkey authentication was cancelled. Please try again or sign in with password.';
        } else if (error.message.includes('Please sign in with password')) {
          errorMessage = error.message;
        } else {
          errorMessage += 'Please sign in with password instead.';
        }
      }
      trackEvent(
        getContentByPath<string>('mfa.analytics.error.eventName'),
        '2fa setup phone number input error',
        getContentByPath<string>('mfa.analytics.error.trackSuffix'),
        {
          errorField: 'Passkey',
          errorDisplayed: errorMessage,
        },
      );

      setErrorState(errorMessage);
    } finally {
      setLoadingPasskeyState(false);
    }
  }, [
    authClient,
    email,
    trackEvent,
    redirectToApp,
    getContentByPath,
    passkeyEnabled,
    mgmId,
    isAllowOktaSignInWithRedirect,
  ]);

  useEffect(() => {
    if (invalidFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (invalidFields.includes('email') && !analyticsFieldErrors.includes('email')) {
        eventAttributes.errorField = 'Email';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.email.error');
      }

      if (invalidFields.includes('password') && !analyticsFieldErrors.includes('password')) {
        eventAttributes.errorField = 'Password';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.password.error');
      }

      if (eventAttributes.errorField) {
        trackEvent(
          'identity – sign in - credentials input error',
          'sign in credentials input error',
          'signininputerror',
          eventAttributes,
        );
      }
    }

    setAnalyticsFieldErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length]);

  const canRequestSignIn = useMemo(
    () => !invalidFields.length && login && password && !isLoading && !isLockedOut,
    [invalidFields.length, isLoading, login, password, isLockedOut],
  );

  if (['password_reset', 'password_reset:error'].includes(postLoginStep)) {
    return (
      <ResetPasswordStyledContent>
        <PageLayout testId='reset-password'>
          <ResetPasswordForm
            isLoading={isLoading || postLoginStep === 'password_reset:error' ? true : false}
            error={error}
            userInfo={authTransaction?.user}
            resetPassword={updatePassword}
          />
        </PageLayout>
      </ResetPasswordStyledContent>
    );
  }

  return (
    <div>
      <PageLayout
        testId={type === 'default' ? 'route-/signin' : 'route-/signin-auth'}
        title={getContentByPath('signIn.title')}
        description={getContentByPath('signIn.description')}
      >
        <Container height='auto'>
          <Logo />
          <StyledHeading variant='heading-medium-3' as='h1' testId='page-title'>
            {getContentByPath('signIn.title')}
          </StyledHeading>
          {error && (
            <StyledAlertContainer>
              <Alert
                open
                hiddenCloseButton
                variant='error'
                message={<Markdown theme={Themes.VEGA}>{error}</Markdown>}
                data-testid='sign-in-error'
                role='alert'
              />
            </StyledAlertContainer>
          )}
          <div>
            <TextInput
              variant='default'
              label={getContentByPath('signIn.fields.email.title')}
              id='email'
              testId='email'
              disabled={type === 'auth-selection' || isLoading || !!email}
              error={invalidFields.includes('email')}
              hint={invalidFields.includes('email') ? getContentByPath('signIn.fields.email.error') : ''}
              {...bindUsername}
              maxLength={MaxLengths.Email}
              icon='lock'
              autoComplete='email'
              type='email'
            />

            {type === 'default' && (
              <>
                <TextInput
                  variant='password'
                  testId='password'
                  label={getContentByPath('signIn.fields.password.title')}
                  id='password'
                  disabled={isLoading}
                  error={invalidFields.includes('password')}
                  hint={invalidFields.includes('password') ? getContentByPath('signIn.fields.password.error') : ''}
                  {...bindPassword}
                  maxLength={MaxLengths.Password}
                  icon='lock'
                  data-private
                  autoComplete='current-password'
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      validateField('password', password);
                      if (!invalidFields.length) {
                        processSignIn(login, password);
                      }
                    }
                  }}
                />
                {!isHideForgotPasswordLink && (
                  <StyledForgotPasswordLink>
                    <TextLink
                      button
                      variant='small'
                      href='/forgot-password'
                      onClick={async e => {
                        e?.preventDefault();
                        await trackEvent(
                          'identity – sign in - forgot password',
                          'forgot password - sign in help',
                          'signinforgotplink',
                          {},
                          true,
                        );
                        navigate('/forgot-password');
                      }}
                    >
                      {getContentByPath('signIn.forgotPassword')}
                    </TextLink>
                  </StyledForgotPasswordLink>
                )}
                {!!legalText && (
                  <StyledLegalText variant='body-regular-m' testId='legal-text'>
                    {legalText}
                  </StyledLegalText>
                )}
                <StyledButton
                  onClick={e => {
                    e?.preventDefault();
                    processSignIn(login, password);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      processSignIn(login, password);
                    }
                  }}
                  disabled={!canRequestSignIn}
                  testId='sign-in'
                  type='submit'
                  loading={isLoading}
                  shape='square'
                  size='large'
                  variant='primary'
                  label={signInCTA}
                  fullWidth
                />
              </>
            )}

            {type === 'auth-selection' && (
              <>
                {passkeyEnabled && isPasskeySupported && hasPasskeyEnrolled && (
                  <StyledButton
                    onClick={e => {
                      e?.preventDefault();
                      processSignInWithPasskey();
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        processSignInWithPasskey();
                      }
                    }}
                    disabled={isLoadingPassword}
                    testId='sign-in-with-passkey'
                    type='submit'
                    loading={isLoadingPasskey}
                    shape='square'
                    size='large'
                    variant='primary'
                    label={signInWithPasskey}
                    fullWidth
                  />
                )}
                <StyledButton
                  onClick={e => {
                    e?.preventDefault();
                    processSignInWithPassword();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      processSignInWithPassword();
                    }
                  }}
                  disabled={isLoadingPasskey}
                  testId='sign-in-with-password'
                  type='submit'
                  loading={isLoadingPassword}
                  shape='square'
                  size='large'
                  variant='secondary'
                  label={signInWithPassword}
                  fullWidth
                />
              </>
            )}
          </div>
        </Container>
      </PageLayout>
    </div>
  );
}

SignIn.displayName = 'SignIn';
